// src/services/configService.ts
import { reportMessage } from "@/utils/sentry";

// 环境变量配置接口
export interface AppConfig {
  // 基础配置
  app: {
    title: string;
    baseUrl: string;
    apiBaseUrl: string;
    domain?: string;
  };

  // 微信配置
  wechat: {
    enabled: boolean;
    appId?: string;
    debug: boolean;
    redirectDomain?: string;
    scope: "snsapi_base" | "snsapi_userinfo";
  };

  // 功能开关
  features: {
    passwordRecovery: boolean;
    systemStatus: boolean;
    versionCheck: boolean;
    pwa: boolean;
    i18n: boolean;
    debugLogs: boolean;
    performanceMonitoring: boolean;
  };

  // 安全配置
  security: {
    sentryDsn?: string;
    sentryEnvironment: string;
    apiTimeout: number;
  };

  // 国际化配置
  i18n: {
    defaultLocale: "zh-CN" | "en-GB";
  };

  // 构建配置
  build: {
    bundleAnalyzer: boolean;
    sourcemap: boolean;
  };
}

export class ConfigService {
  private config: AppConfig;
  private isInitialized: boolean = false;

  constructor() {
    this.config = this.loadConfig();
    this.validateConfig();
    this.logConfigStatus();
    this.isInitialized = true;
  }

  /**
   * 获取应用配置
   */
  public getConfig(): Readonly<AppConfig> {
    return Object.freeze({ ...this.config });
  }

  /**
   * 获取特定配置节
   */
  public getAppConfig() {
    return this.config.app;
  }

  public getWechatConfig() {
    return this.config.wechat;
  }

  public getFeaturesConfig() {
    return this.config.features;
  }

  public getSecurityConfig() {
    return this.config.security;
  }

  public getI18nConfig() {
    return this.config.i18n;
  }

  public getBuildConfig() {
    return this.config.build;
  }

  /**
   * 检查微信功能是否启用
   */
  public isWechatEnabled(): boolean {
    return this.config.wechat.enabled;
  }

  /**
   * 检查是否为开发环境
   */
  public isDevelopment(): boolean {
    return import.meta.env.DEV;
  }

  /**
   * 检查是否为生产环境
   */
  public isProduction(): boolean {
    return import.meta.env.PROD;
  }

  /**
   * 获取当前环境模式
   */
  public getMode(): string {
    return import.meta.env.MODE;
  }

  /**
   * 检查功能是否启用
   */
  public isFeatureEnabled(feature: keyof AppConfig["features"]): boolean {
    return this.config.features[feature];
  }

  /**
   * 重新加载配置（热更新）
   */
  public reloadConfig(): void {
    const newConfig = this.loadConfig();
    const oldConfig = { ...this.config };

    this.config = newConfig;
    this.validateConfig();

    // 检查关键配置是否发生变化
    this.checkConfigChanges(oldConfig, newConfig);

    reportMessage("配置已重新加载", "info", {
      action: "config_reload",
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * 从环境变量加载配置
   */
  private loadConfig(): AppConfig {
    return {
      app: {
        title: this.getEnv("VITE_APP_TITLE", "Aberdeen University SSO"),
        baseUrl: this.getEnv("VITE_BASE_URL", "/"),
        apiBaseUrl: this.getEnv("VITE_API_BASE_URL", ""),
        // domain 字段已移除，使用Cookie服务的智能配置
      },

      wechat: {
        enabled: this.getBooleanEnv("VITE_ENABLE_WECHAT", false),
        appId: this.getEnv("VITE_WECHAT_APPID"),
        debug: this.getBooleanEnv("VITE_WECHAT_DEBUG", this.isDevelopment()),
        redirectDomain: this.getEnv("VITE_WECHAT_REDIRECT_DOMAIN"),
        scope: this.getEnv("VITE_WECHAT_SCOPE", "snsapi_base") as
          | "snsapi_base"
          | "snsapi_userinfo",
      },

      features: {
        passwordRecovery: this.getBooleanEnv(
          "VITE_ENABLE_PASSWORD_RECOVERY",
          true,
        ),
        systemStatus: this.getBooleanEnv("VITE_ENABLE_SYSTEM_STATUS", true),
        versionCheck: this.getBooleanEnv("VITE_ENABLE_VERSION_CHECK", true),
        pwa: this.getBooleanEnv("VITE_ENABLE_PWA", false),
        i18n: this.getBooleanEnv("VITE_ENABLE_I18N", true),
        debugLogs: this.getBooleanEnv(
          "VITE_ENABLE_DEBUG_LOGS",
          this.isDevelopment(),
        ),
        performanceMonitoring: this.getBooleanEnv(
          "VITE_ENABLE_PERFORMANCE_MONITORING",
          false,
        ),
      },

      security: {
        sentryDsn: this.getEnv("VITE_SENTRY_DSN"),
        sentryEnvironment: this.getEnv(
          "VITE_SENTRY_ENVIRONMENT",
          this.isDevelopment() ? "development" : "production",
        ),
        apiTimeout: this.getNumberEnv("VITE_API_TIMEOUT", 10000),
      },

      i18n: {
        defaultLocale: this.getEnv("VITE_DEFAULT_LOCALE", "zh-CN") as
          | "zh-CN"
          | "en-GB",
      },

      build: {
        bundleAnalyzer: this.getBooleanEnv(
          "VITE_ENABLE_BUNDLE_ANALYZER",
          false,
        ),
        sourcemap: this.getBooleanEnv(
          "VITE_ENABLE_SOURCEMAP",
          this.isDevelopment(),
        ),
      },
    };
  }

  /**
   * 获取环境变量（字符串类型）
   */
  private getEnv(key: string, defaultValue?: string): string | undefined {
    const value = import.meta.env[key];
    return value !== undefined ? value : defaultValue;
  }

  /**
   * 获取环境变量（布尔类型）
   */
  private getBooleanEnv(key: string, defaultValue: boolean = false): boolean {
    const value = import.meta.env[key];
    if (value === undefined) return defaultValue;

    // 处理各种可能的布尔值表示
    const normalizedValue = value.toString().toLowerCase().trim();
    return (
      normalizedValue === "true" ||
      normalizedValue === "1" ||
      normalizedValue === "yes" ||
      normalizedValue === "on"
    );
  }

  /**
   * 获取环境变量（数字类型）
   */
  private getNumberEnv(key: string, defaultValue: number): number {
    const value = import.meta.env[key];
    if (value === undefined) return defaultValue;

    const parsed = parseInt(value.toString(), 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  /**
   * 验证配置有效性
   */
  private validateConfig(): void {
    const errors: string[] = [];

    // 验证基础配置
    if (!this.config.app.apiBaseUrl) {
      errors.push("API 基础 URL 未配置 (VITE_API_BASE_URL)");
    }

    // 验证微信配置
    if (this.config.wechat.enabled) {
      if (!this.config.wechat.appId) {
        errors.push("微信功能已启用但 AppID 未配置 (VITE_WECHAT_APPID)");
      }

      if (!this.config.wechat.redirectDomain) {
        console.warn(
          "微信重定向域名未配置 (VITE_WECHAT_REDIRECT_DOMAIN)，将使用当前域名",
        );
      }

      // 验证微信授权作用域
      if (
        !["snsapi_base", "snsapi_userinfo"].includes(this.config.wechat.scope)
      ) {
        errors.push(
          "微信授权作用域配置无效 (VITE_WECHAT_SCOPE)，应为 snsapi_base 或 snsapi_userinfo",
        );
      }
    }

    // 验证国际化配置
    if (this.config.features.i18n) {
      if (!["zh-CN", "en-GB"].includes(this.config.i18n.defaultLocale)) {
        errors.push(
          "默认语言配置无效 (VITE_DEFAULT_LOCALE)，应为 zh-CN 或 en-GB",
        );
      }
    }

    // 验证 API 超时配置
    if (this.config.security.apiTimeout < 1000) {
      console.warn("API 超时时间配置过短，建议至少设置为 5000ms");
    }

    // 如果有错误，报告并抛出异常
    if (errors.length > 0) {
      const errorMessage = "配置验证失败:\n" + errors.join("\n");
      reportMessage(errorMessage, "error", {
        action: "config_validation_error",
        errors,
      });
      throw new Error(errorMessage);
    }
  }

  /**
   * 检查配置变化
   */
  private checkConfigChanges(oldConfig: AppConfig, newConfig: AppConfig): void {
    const changes: string[] = [];

    // 检查微信功能开关变化
    if (oldConfig.wechat.enabled !== newConfig.wechat.enabled) {
      changes.push(
        `微信功能: ${oldConfig.wechat.enabled} -> ${newConfig.wechat.enabled}`,
      );
    }

    // 检查 API 基础 URL 变化
    if (oldConfig.app.apiBaseUrl !== newConfig.app.apiBaseUrl) {
      changes.push(
        `API URL: ${oldConfig.app.apiBaseUrl} -> ${newConfig.app.apiBaseUrl}`,
      );
    }

    // 检查调试模式变化
    if (oldConfig.features.debugLogs !== newConfig.features.debugLogs) {
      changes.push(
        `调试日志: ${oldConfig.features.debugLogs} -> ${newConfig.features.debugLogs}`,
      );
    }

    if (changes.length > 0) {
      reportMessage("检测到配置变化", "info", {
        action: "config_changes_detected",
        changes,
      });
    }
  }

  /**
   * 记录配置状态
   */
  private logConfigStatus(): void {
    const sensitiveFields = ["sentryDsn", "appId"]; // 敏感字段列表

    const safeConfig = this.maskSensitiveData(this.config, sensitiveFields);

    reportMessage("应用配置已加载", "info", {
      action: "config_loaded",
      mode: this.getMode(),
      wechatEnabled: this.config.wechat.enabled,
      debugMode: this.config.features.debugLogs,
      config: safeConfig,
    });

    // 在开发环境下打印配置信息
    if (this.isDevelopment() && this.config.features.debugLogs) {
      console.group("📋 应用配置信息");
      console.log("🔧 模式:", this.getMode());
      console.log("🌐 API URL:", this.config.app.apiBaseUrl);
      console.log(
        "💬 微信功能:",
        this.config.wechat.enabled ? "✅ 启用" : "❌ 禁用",
      );
      console.log(
        "🐛 调试模式:",
        this.config.features.debugLogs ? "✅ 启用" : "❌ 禁用",
      );
      console.log("🔒 配置详情:", safeConfig);
      console.groupEnd();
    }
  }

  /**
   * 遮盖敏感数据
   */
  private maskSensitiveData(obj: unknown, sensitiveFields: string[]): unknown {
    if (typeof obj !== "object" || obj === null) {
      return obj;
    }

    const masked = { ...obj };

    for (const key in masked) {
      if (sensitiveFields.includes(key) && typeof masked[key] === "string") {
        const value = masked[key];
        if (value && value.length > 4) {
          masked[key] = value.substring(0, 4) + "*".repeat(value.length - 4);
        }
      } else if (typeof masked[key] === "object") {
        masked[key] = this.maskSensitiveData(masked[key], sensitiveFields);
      }
    }

    return masked;
  }

  /**
   * 获取配置摘要（用于监控和调试）
   */
  public getConfigSummary(): Record<string, any> {
    return {
      mode: this.getMode(),
      wechatEnabled: this.config.wechat.enabled,
      featuresEnabled: Object.entries(this.config.features)
        .filter(([, enabled]) => enabled)
        .map(([feature]) => feature),
      hasApiUrl: !!this.config.app.apiBaseUrl,
      hasDomain: !!this.config.app.domain,
      locale: this.config.i18n.defaultLocale,
      isInitialized: this.isInitialized,
    };
  }
}

// 全局配置服务实例
export const configService = new ConfigService();
